import React, { forwardRef } from 'react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'destructive';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Left-aligned icon (renders at the start of the button). */
  leadingIcon?: React.ReactNode;
  /** Right-aligned icon. */
  trailingIcon?: React.ReactNode;
  /** Replaces button content with a spinner and disables interaction. */
  loading?: boolean;
  /** Full-width block button. */
  block?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Brand primary — respects inspector brand color via --brand.
  primary:
    'bg-brand text-brand-ink shadow-xs hover:brightness-[0.96] active:brightness-[0.92] disabled:bg-ink-faint disabled:text-white',
  // Neutral secondary — paper-white on warm border. Reads as "refined neutral".
  secondary:
    'bg-surface-1 text-ink border border-border-strong shadow-xs hover:bg-surface-2 active:bg-surface-3 disabled:text-ink-faint disabled:bg-surface-2',
  // Ghost — no background until hover.
  ghost:
    'bg-transparent text-ink hover:bg-surface-2 active:bg-surface-3 disabled:text-ink-faint',
  // Subtle — soft brand wash, less shouty than primary.
  subtle:
    'bg-brand-soft text-brand hover:brightness-95 disabled:text-ink-faint disabled:bg-surface-2',
  // Destructive — uses critical severity tokens.
  destructive:
    'bg-sev-critical text-white shadow-xs hover:brightness-[0.94] active:brightness-[0.88] disabled:bg-ink-faint',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-body-sm gap-1.5 rounded-md',
  sm: 'h-8 px-3 text-body-sm gap-1.5 rounded-md',
  md: 'h-10 px-4 text-body-md gap-2 rounded-lg',
  lg: 'h-12 px-5 text-body-lg gap-2 rounded-lg',
};

const ICON_SIZE: Record<ButtonSize, string> = {
  xs: 'h-3.5 w-3.5', sm: 'h-4 w-4', md: 'h-[18px] w-[18px]', lg: 'h-5 w-5',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    loading = false,
    block = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      className={cn(
        'inline-flex items-center justify-center font-medium whitespace-nowrap',
        'transition-all duration-180 ease-premium',
        'disabled:cursor-not-allowed',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          className={cn(
            'inline-block rounded-full border-2 border-current border-t-transparent animate-spin',
            ICON_SIZE[size],
          )}
          aria-hidden
        />
      ) : (
        leadingIcon && (
          <span className={cn('shrink-0 flex items-center', ICON_SIZE[size])} aria-hidden>
            {leadingIcon}
          </span>
        )
      )}
      {children && <span className="min-w-0 truncate">{children}</span>}
      {!loading && trailingIcon && (
        <span className={cn('shrink-0 flex items-center', ICON_SIZE[size])} aria-hidden>
          {trailingIcon}
        </span>
      )}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'leadingIcon' | 'trailingIcon' | 'children'> {
  /** Required for a11y — describes the icon action. */
  label: string;
  icon: React.ReactNode;
}

const ICON_BUTTON_SIZE: Record<ButtonSize, string> = {
  xs: 'h-7 w-7',  sm: 'h-8 w-8',  md: 'h-10 w-10', lg: 'h-12 w-12',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', variant = 'ghost', className, ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      size={size}
      variant={variant}
      aria-label={label}
      className={cn('!px-0', ICON_BUTTON_SIZE[size], className)}
      {...rest}
    >
      <span className={cn('flex items-center justify-center', ICON_SIZE[size])} aria-hidden>
        {icon}
      </span>
    </Button>
  );
});
